import s from './check-form.module.scss';
import { Input } from '@/ui/input/input';
import PhoneIcon from '@/icons/common/phone.svg';
import { Button } from '@/ui/button/button';
import ReCAPTCHA from 'react-google-recaptcha';
import { ChangeEvent, FormEvent, useRef, useState } from 'react';
import xiorInstance from '@/shared/api/instance';
import { XiorError } from 'xior';
import { useStatistics } from '@/hooks/common/use-statistics';
import { useTranslations } from 'next-intl';
import { luhnValidate } from '@/utils/helpers/validate-imei-sn';
import { QueryObserverResult } from '@tanstack/query-core';
import { AttemptsData } from '@/modules/loading-page/types';
import HelpIcon from '@/icons/common/help.svg';
import AssignIcon from '@/icons/common/assignment.svg';
import dynamic from 'next/dynamic';
import clsx from 'clsx';

const LoadingModal = dynamic(
  () =>
    import('@/shared/components/loading-modal/loading-modal').then(
      (mod) => mod.default
    ),
  {
    ssr: false,
  }
);

const WhereFindImei = dynamic(
  () =>
    import('@/shared/components/where-find-imei/where-find-imei').then(
      (mod) => mod.default
    ),
  {
    ssr: false,
  }
);

const FreeSample = dynamic(
  () =>
    import('@/shared/components/free-sample/free-sample').then(
      (mod) => mod.default
    ),
  {
    ssr: false,
  }
);

interface CheckFormProps {
  checkAttempts: () => Promise<QueryObserverResult<AttemptsData, Error>>;
  fullWidth?: boolean;
  centeredHint?: boolean;
}

const CheckForm = ({
  checkAttempts,
  fullWidth = false,
  centeredHint = false,
}: CheckFormProps) => {
  const tC = useTranslations('Common');
  const getStatistic = useStatistics(false);

  const [shouldLoadScript, setShouldLoadScript] = useState(false);
  const [openedSample, setOpenedSample] = useState(false);
  const [openedWhere, setOpenedWhere] = useState(false);
  const [openedLoading, setOpenedLoading] = useState(null);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [{ value, error, touched }, setFormValue] = useState({
    value: '',
    error: '',
    touched: false,
  });

  const recaptchaRef = useRef<any>(null);

  const checkForError = () => {
    if (!value || value.length < 8) {
      return tC('min8');
    }

    if (value.length > 15) {
      return tC('max15');
    }

    if (value.length >= 8 && value.length <= 14) {
      return !!value.match(/^([A-Z0-9]{8,14})$/i) ? '' : tC('incorrectSN');
    }

    if (value.length === 15) {
      return luhnValidate(value) ? '' : tC('incorrectIMEI');
    }

    return '';
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    event.target.value = event.target.value
      .replace(/[^0-9a-z]/gi, '')
      .toUpperCase()
      .slice(0, 15);

    setFormValue((prev) => ({
      ...prev,
      error: touched ? checkForError() : '',
      value: event.target.value,
    }));
  };

  const handleBlur = () => {
    setFormValue((prev) => ({
      ...prev,
      touched: true,
      error: checkForError(),
    }));
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const error = checkForError();
    setFormValue((prev) => ({
      ...prev,
      touched: true,
      error: error,
    }));

    if (error) return;

    setIsSubmitted(true);

    const { data: attempts } = await checkAttempts();

    if (attempts?.remaining_attempts === 0) {
      setIsSubmitted(false);
      return;
    }

    const tokenAsync = await recaptchaRef.current?.executeAsync();

    recaptchaRef.current?.reset();

    const body = {
      device_id: value,
      'g-recaptcha-response': tokenAsync,
    };

    // @ts-ignore
    const tok = window['r7s8a9q0w1e2r3t4y5u6i7o8p9'] as string;
    try {
      const res = await xiorInstance.post(`/checks/free`, body, {
        headers: {
          'X-hash': tok,
        },
      });

      setOpenedLoading(res.data.id);
      setFormValue({
        value: '',
        error: '',
        touched: false,
      });
      getStatistic();
    } catch (e) {
      const error = e as XiorError;

      if (!error.response) {
        console.log('Unknown', error);
        return;
      }

      console.log(error.response.status);
      console.log(error.response.data);
    } finally {
      setIsSubmitted(false);
    }
  };

  const toggleOpenWhere = async () => {
    if (openedWhere) {
      const { enableScroll } = await import('@/utils/helpers/disable-scroll');
      enableScroll();
    }

    setOpenedWhere((prev) => !prev);
  };

  const toggleOpenSample = async () => {
    if (openedSample) {
      const { enableScroll } = await import('@/utils/helpers/disable-scroll');
      enableScroll();
    }

    setOpenedSample((prev) => !prev);
  };

  const closeOpenedLoading = async () => {
    const { enableScroll } = await import('@/utils/helpers/disable-scroll');
    enableScroll();

    setOpenedLoading(null);
  };

  const handleLoad = () => {
    // if (document.querySelector('#page-script')) {
    //   return;
    // }

    setShouldLoadScript(true);
  };

  return (
    <>
      <form className={s.form} onSubmit={handleSubmit}>
        <div className={clsx(s.checkBlock, { [s.fullWidth]: fullWidth })}>
          <Input
            placeholder={tC('imeiPlaceholder')}
            startAdornment={<PhoneIcon />}
            className={s.imeiField}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched && error}
            onFocus={handleLoad}
            maxLength={15}
            value={value}
            name="imei"
          />
          <div className={s.buttonsWrap}>
            <Button
              size="large"
              className={s.checkBtn}
              disabled={isSubmitted}
              type="submit"
            >
              {tC('ctaButton')}
            </Button>
            {/*<Button*/}
            {/*  size="large"*/}
            {/*  className={s.pricingBtn}*/}
            {/*  type="button"*/}
            {/*  variant={'outlined-white'}*/}
            {/*>*/}
            {/*  {tC('pricingButton')}*/}
            {/*</Button>*/}
          </div>
          {shouldLoadScript && (
            <>
              <div className={s.recaptchaContainer}>
                <ReCAPTCHA
                  ref={recaptchaRef}
                  size="invisible"
                  sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY as string}
                  badge="bottomright"
                />
              </div>
              {/* <AdScript /> */}
            </>
          )}
        </div>
      </form>
      <div className={clsx(s.actions, { [s.centeredHint]: centeredHint })}>
        <button className={s.flexL} type="button" onClick={toggleOpenWhere}>
          <HelpIcon />
          <span>{tC('findImei')}</span>
        </button>
        {/* <button className={s.flexL} type="button" onClick={toggleOpenSample}>
          <AssignIcon />
          <span>{tC('freeSample')}</span>
        </button> */}
      </div>

      {openedWhere && <WhereFindImei handleClose={toggleOpenWhere} />}
      {openedSample && <FreeSample handleClose={toggleOpenSample} />}
      {openedLoading && (
        <LoadingModal
          handleClose={closeOpenedLoading}
          checkID={openedLoading}
        />
      )}
    </>
  );
};

export default CheckForm;
